import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Button,
  TablePagination,
  InputAdornment,
  Grid,
  Card,
  CardContent,
  Stack,
  FormControlLabel,
  Switch,
  IconButton
} from "@mui/material";

import {
  Search,
  Print,
  RestartAlt,
  TrendingUp,
  AccountBalanceWallet,
  ReceiptLong,
  PendingActions,
  CalendarMonth
} from "@mui/icons-material";

import { collection, onSnapshot, query, orderBy } from "firebase/firestore";
import { db } from "../../firebase";

function Outstaningreport() {
  const [customers, setCustomers] = useState([]);
  const [trips, setTrips] = useState([]);
  const [payments, setPayments] = useState([]);

  // Compute 1st Day and Last Day of Current Month
  const getDefaultDates = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();

    const fromStr = `${year}-${String(month + 1).padStart(2, "0")}-01`;
    const lastDay = new Date(year, month + 1, 0);
    const toStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(
      lastDay.getDate()
    ).padStart(2, "0")}`;

    return { fromStr, toStr };
  };

  const defaultDates = useMemo(() => getDefaultDates(), []);
  const [fromDate, setFromDate] = useState(defaultDates.fromStr);
  const [toDate, setToDate] = useState(defaultDates.toStr);

  const [search, setSearch] = useState("");
  const [hideZero, setHideZero] = useState(true); // Automatically hides 0 balance accounts
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isPrinting, setIsPrinting] = useState(false);

  // Realtime Firestore listeners
  useEffect(() => {
    const unsubCustomers = onSnapshot(
      query(collection(db, "customers"), orderBy("customerName", "asc")),
      (snap) => {
        setCustomers(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      }
    );

    const unsubTrips = onSnapshot(collection(db, "trips"), (snap) => {
      setTrips(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });

    const unsubPayments = onSnapshot(collection(db, "customer_history"), (snap) => {
      setPayments(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });

    return () => {
      unsubCustomers();
      unsubTrips();
      unsubPayments();
    };
  }, []);

  // Safe timestamp helper
  const getMillis = (dateVal) => {
    if (!dateVal) return 0;
    if (typeof dateVal.toMillis === "function") return dateVal.toMillis();
    if (dateVal.toDate) return dateVal.toDate().getTime();
    return new Date(dateVal).getTime();
  };

  // Compute Balances
  const computedData = useMemo(() => {
    const startMs = fromDate ? new Date(`${fromDate}T00:00:00`).getTime() : 0;
    const endMs = toDate ? new Date(`${toDate}T23:59:59.999`).getTime() : Infinity;

    return customers.map((customer) => {
      const custTrips = trips.filter((t) => t.customerId === customer.id);
      const custPayments = payments.filter((p) => p.customerId === customer.id);

      // Previous Outstanding (Before fromDate)
      const prevSale = custTrips
        .filter((t) => getMillis(t.date || t.createdAt) < startMs)
        .reduce(
          (sum, trip) =>
            sum +
            (trip.products?.reduce((s, p) => s + Number(p.amount || 0), 0) ||
              Number(trip.totalAmount || 0)),
          0
        );

      const prevPaid = custPayments
        .filter((p) => getMillis(p.date) < startMs)
        .reduce((sum, p) => sum + Number(p.amount || 0), 0);

      const previousOutstanding = prevSale - prevPaid;

      // Period Transactions (Between fromDate and toDate)
      const periodSale = custTrips
        .filter((t) => {
          const m = getMillis(t.date || t.createdAt);
          return m >= startMs && m <= endMs;
        })
        .reduce(
          (sum, trip) =>
            sum +
            (trip.products?.reduce((s, p) => s + Number(p.amount || 0), 0) ||
              Number(trip.totalAmount || 0)),
          0
        );

      const periodPaid = custPayments
        .filter((p) => {
          const m = getMillis(p.date);
          return m >= startMs && m <= endMs;
        })
        .reduce((sum, p) => sum + Number(p.amount || 0), 0);

      const periodRemaining = periodSale - periodPaid;
      const totalOutstanding = previousOutstanding + periodRemaining;

      return {
        id: customer.id,
        customerName: customer.customerName || "Unnamed Customer",
        mobile: customer.mobile || "-",
        previousOutstanding,
        periodSale,
        periodPaid,
        periodRemaining,
        totalOutstanding
      };
    });
  }, [customers, trips, payments, fromDate, toDate]);

  // Filter out 0 balances and apply search
  const filteredCustomers = useMemo(() => {
    const term = search.trim().toLowerCase();
    return computedData.filter((c) => {
      // Hide 0 outstanding check
      if (hideZero && Math.round(Number(c.totalOutstanding || 0)) === 0) {
        return false;
      }
      return (
        c.customerName?.toLowerCase().includes(term) ||
        c.mobile?.toLowerCase().includes(term)
      );
    });
  }, [computedData, search, hideZero]);

  // Calculate Column Totals
  const summaryTotals = useMemo(() => {
    return filteredCustomers.reduce(
      (acc, curr) => {
        acc.prev += curr.previousOutstanding;
        acc.sale += curr.periodSale;
        acc.paid += curr.periodPaid;
        acc.remaining += curr.periodRemaining;
        acc.total += curr.totalOutstanding;
        return acc;
      },
      { prev: 0, sale: 0, paid: 0, remaining: 0, total: 0 }
    );
  }, [filteredCustomers]);

  const formatMoney = (n) =>
    `₹${Number(n || 0).toLocaleString("en-IN", {
      maximumFractionDigits: 0
    })}`;

  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return "-";
    const parts = dateStr.split("-");
    if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
    return dateStr;
  };

  const handlePrint = () => {
    setIsPrinting(true);
    setTimeout(() => {
      window.print();
      setIsPrinting(false);
    }, 150);
  };

  const handleResetFilter = () => {
    setFromDate(defaultDates.fromStr);
    setToDate(defaultDates.toStr);
    setSearch("");
    setHideZero(true);
    setPage(0);
  };

  const tightCell = { py: 0.8, px: 1.2, fontSize: "0.82rem" };

  // Print renders all matching items
  const displayedRows = isPrinting
    ? filteredCustomers
    : filteredCustomers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ backgroundColor: "#f8fafc", minHeight: "100vh", p: { xs: 1.5, sm: 2.5 } }}>
      {/* Print Specific CSS */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          .printable-area, .printable-area * { visibility: visible; }
          .printable-area { 
            position: absolute; 
            left: 0; 
            top: 0; 
            width: 100%; 
            border: none !important; 
          }
          .no-print { display: none !important; }
          @page { size: A4; margin: 10mm; size: landscape; }
        }
      `}</style>

      {/* Screen Controls Header */}
      <Box className="no-print" mb={2.5}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={2}
          mb={2}
        >
          <Box>
            <Typography variant="h5" fontWeight="700" color="#0f172a">
              Outstanding Report
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Date-wise billing, collections, previous balance, and net outstanding
            </Typography>
          </Box>

          <Stack direction="row" spacing={1.5}>
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<RestartAlt />}
              onClick={handleResetFilter}
              size="small"
              sx={{ bgcolor: "white", borderColor: "#cbd5e1" }}
            >
              Reset
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Print />}
              onClick={handlePrint}
              size="small"
              disableElevation
              sx={{ bgcolor: "#2563eb", "&:hover": { bgcolor: "#1d4ed8" } }}
            >
              Print Report
            </Button>
          </Stack>
        </Stack>

        {/* Filter Controls */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 2,
            border: "1px solid #e2e8f0",
            bgcolor: "white"
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                size="small"
                label="From Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={fromDate}
                onChange={(e) => {
                  setFromDate(e.target.value);
                  setPage(0);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                size="small"
                label="To Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={toDate}
                onChange={(e) => {
                  setToDate(e.target.value);
                  setPage(0);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by customer or mobile..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={hideZero}
                    onChange={(e) => {
                      setHideZero(e.target.checked);
                      setPage(0);
                    }}
                    color="primary"
                    size="small"
                  />
                }
                label={<Typography variant="body2" fontWeight="500">Hide 0 Bal</Typography>}
              />
            </Grid>
          </Grid>
        </Paper>
      </Box>

      {/* Modern KPI Cards */}
      <Grid container spacing={2} mb={2.5} className="no-print">
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: 2 }}>
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight="600">
                    PREVIOUS OUTSTANDING
                  </Typography>
                  <Typography variant="h6" fontWeight="bold" color="#334155">
                    {formatMoney(summaryTotals.prev)}
                  </Typography>
                </Box>
                <IconButton size="small" sx={{ bgcolor: "#f1f5f9", color: "#475569" }}>
                  <TrendingUp fontSize="small" />
                </IconButton>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: 2 }}>
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight="600">
                    PERIOD SALES
                  </Typography>
                  <Typography variant="h6" fontWeight="bold" color="#0284c7">
                    {formatMoney(summaryTotals.sale)}
                  </Typography>
                </Box>
                <IconButton size="small" sx={{ bgcolor: "#e0f2fe", color: "#0284c7" }}>
                  <ReceiptLong fontSize="small" />
                </IconButton>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: 2 }}>
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight="600">
                    PERIOD PAID
                  </Typography>
                  <Typography variant="h6" fontWeight="bold" color="#16a34a">
                    {formatMoney(summaryTotals.paid)}
                  </Typography>
                </Box>
                <IconButton size="small" sx={{ bgcolor: "#dcfce7", color: "#16a34a" }}>
                  <AccountBalanceWallet fontSize="small" />
                </IconButton>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={0} sx={{ border: "1px solid #fee2e2", bgcolor: "#fff5f5", borderRadius: 2 }}>
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="caption" color="error.dark" fontWeight="600">
                    NET OUTSTANDING
                  </Typography>
                  <Typography variant="h6" fontWeight="bold" color="#dc2626">
                    {formatMoney(summaryTotals.total)}
                  </Typography>
                </Box>
                <IconButton size="small" sx={{ bgcolor: "#fee2e2", color: "#dc2626" }}>
                  <PendingActions fontSize="small" />
                </IconButton>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Printable Report Section */}
      <Box className="printable-area">
        {/* Date Duration Header (Visible on Screen & Print) */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 1.5,
            mb: 1.5,
            borderRadius: 1.5,
            bgcolor: "#f1f5f9",
            border: "1px solid #e2e8f0"
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <CalendarMonth fontSize="small" color="primary" />
            <Typography variant="subtitle2" fontWeight="700" color="#1e293b">
              REPORT PERIOD: {formatDateDisplay(fromDate)} TO {formatDateDisplay(toDate)}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">
            Showing {filteredCustomers.length} Customer Accounts {hideZero && "(Excluding ₹0 Balance)"}
          </Typography>
        </Box>

        {/* Table Content */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 2,
            border: "1px solid #e2e8f0",
            overflow: "hidden",
            bgcolor: "white"
          }}
        >
          <Table size="small">
            <TableHead sx={{ bgcolor: "#1e293b" }}>
              <TableRow>
                <TableCell sx={{ ...tightCell, color: "white", fontWeight: "700", width: "40px" }}>#</TableCell>
                <TableCell sx={{ ...tightCell, color: "white", fontWeight: "700" }}>Customer Name</TableCell>
                <TableCell sx={{ ...tightCell, color: "white", fontWeight: "700", width: "120px" }}>Mobile</TableCell>
                <TableCell align="right" sx={{ ...tightCell, color: "white", fontWeight: "700" }}>
                  Previous Outstanding
                </TableCell>
                <TableCell align="right" sx={{ ...tightCell, color: "white", fontWeight: "700" }}>
                  Period Sale
                </TableCell>
                <TableCell align="right" sx={{ ...tightCell, color: "white", fontWeight: "700" }}>
                  Period Paid
                </TableCell>
                <TableCell align="right" sx={{ ...tightCell, color: "white", fontWeight: "700" }}>
                  Period Balance
                </TableCell>
                <TableCell align="right" sx={{ ...tightCell, color: "white", fontWeight: "700", bgcolor: "#0f172a" }}>
                  Total Outstanding
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {displayedRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4, color: "text.secondary" }}>
                    No outstanding accounts found for this period.
                  </TableCell>
                </TableRow>
              ) : (
                displayedRows.map((row, index) => (
                  <TableRow
                    key={row.id}
                    hover
                    sx={{
                      "&:nth-of-type(even)": { bgcolor: "#f8fafc" },
                      "&:hover": { bgcolor: "#f1f5f9" }
                    }}
                  >
                    <TableCell sx={tightCell}>
                      {isPrinting ? index + 1 : page * rowsPerPage + index + 1}
                    </TableCell>
                    <TableCell sx={tightCell}>
                      <Typography variant="body2" fontWeight="600" color="#0f172a">
                        {row.customerName}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ ...tightCell, color: "text.secondary" }}>
                      {row.mobile}
                    </TableCell>
                    <TableCell align="right" sx={{ ...tightCell, color: "#475569" }}>
                      {formatMoney(row.previousOutstanding)}
                    </TableCell>
                    <TableCell align="right" sx={{ ...tightCell, fontWeight: "500" }}>
                      {formatMoney(row.periodSale)}
                    </TableCell>
                    <TableCell align="right" sx={{ ...tightCell, fontWeight: "500", color: "#16a34a" }}>
                      {formatMoney(row.periodPaid)}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        ...tightCell,
                        fontWeight: "600",
                        color: row.periodRemaining > 0 ? "#b45309" : "#64748b"
                      }}
                    >
                      {formatMoney(row.periodRemaining)}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        ...tightCell,
                        bgcolor: row.totalOutstanding > 0 ? "#fff1f2" : "#f0fdf4"
                      }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight="700"
                        color={row.totalOutstanding > 0 ? "#dc2626" : "#16a34a"}
                      >
                        {formatMoney(row.totalOutstanding)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}

              {/* Grand Total Row */}
              {filteredCustomers.length > 0 && (
                <TableRow sx={{ bgcolor: "#f1f5f9", fontWeight: "bold" }}>
                  <TableCell colSpan={3} sx={{ ...tightCell, fontWeight: "bold" }} align="right">
                    Grand Total:
                  </TableCell>
                  <TableCell align="right" sx={{ ...tightCell, fontWeight: "bold" }}>
                    {formatMoney(summaryTotals.prev)}
                  </TableCell>
                  <TableCell align="right" sx={{ ...tightCell, fontWeight: "bold", color: "#0284c7" }}>
                    {formatMoney(summaryTotals.sale)}
                  </TableCell>
                  <TableCell align="right" sx={{ ...tightCell, fontWeight: "bold", color: "#16a34a" }}>
                    {formatMoney(summaryTotals.paid)}
                  </TableCell>
                  <TableCell align="right" sx={{ ...tightCell, fontWeight: "bold" }}>
                    {formatMoney(summaryTotals.remaining)}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      ...tightCell,
                      fontWeight: "bold",
                      color: summaryTotals.total > 0 ? "#dc2626" : "#16a34a",
                      fontSize: "0.9rem"
                    }}
                  >
                    {formatMoney(summaryTotals.total)}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          {/* Pagination Controls */}
          <TablePagination
            className="no-print"
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={filteredCustomers.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            sx={{
              borderTop: "1px solid #e2e8f0",
              ".MuiTablePagination-toolbar": { minHeight: "40px" }
            }}
          />
        </Paper>

        {/* Print Signatures */}
        <Box sx={{ display: { xs: "none", print: "block" }, mt: 6, px: 2 }}>
          <Stack direction="row" justifyContent="space-between">
            <Box textAlign="center">
              <Typography variant="body2">_________________________</Typography>
              <Typography variant="caption" fontWeight="bold">Prepared By</Typography>
            </Box>
            <Box textAlign="center">
              <Typography variant="body2">_________________________</Typography>
              <Typography variant="caption" fontWeight="bold">Checked By</Typography>
            </Box>
            <Box textAlign="center">
              <Typography variant="body2">_________________________</Typography>
              <Typography variant="caption" fontWeight="bold">Authorized Signatory</Typography>
            </Box>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
}

export default Outstaningreport;